import { getSnapshot } from 'mobx-state-tree';
import { useChat } from 'ai/react';
// import { Auth } from 'aws-amplify';
import { useEffect, useLayoutEffect, useMemo, useState, useRef, useCallback, } from 'react';
import { v4 } from 'uuid';
import { useConversationStore } from '../ConversationStoreProvider';
import { useSelectionWizard } from './wizard/SelectionWizardContext';
import digestMessage from '../digestMessage';
import getOrderData from './product/getOrderData';
import { formatOrdersForAI } from './utils/formatOrders';
import { normalizeMessagesForAddToolResult } from './normalizeMessagesForAddToolResult';
import { getChatMessageTextForSync } from './getChatMessageTextForSync';
import { shouldSelectionWizardOccupyMainColumn, extractSelectionWizardToolCallId, } from './wizard/selectionWizardComposerUtils';
import { buildClientAIRequestArguments } from './buildClientAIRequestArguments';
const hasLocalStorage = typeof localStorage !== 'undefined';
const conversationKey = (store) => (`microcasting.${store.aiMode}.${store.currentPortalID}`);
// Helper to normalize search query for comparison
const normalizeSearchQuery = (query) => {
    if (!query)
        return '';
    return query.trim().toLowerCase();
};
const loadFromLocalStorage = (key) => {
    if (!hasLocalStorage) {
        return undefined;
    }
    const data = localStorage.getItem(key);
    const now = Date.now();
    // if data.updated is more than 6 hours old, return undefined
    // 6 hours (6 * 60 * 60 * 1000)
    // 1 minute (60 * 1000)
    if (data) {
        const parsedData = JSON.parse(data);
        if (parsedData?.updatedAt && now - parsedData.updatedAt > (6 * 60 * 60 * 1000)) {
            return undefined;
        }
        return parsedData;
    }
    return undefined;
};
const useConversation = (options, adminOptions, initialMessages = undefined) => {
    // console.log('>>>>>>>>>>>>> useConversation', options, adminOptions, initialMessages);
    const [error, setError] = useState(null);
    const store = useConversationStore();
    const { requestInlineWizard, pendingInlineWizard, pendingComposerEdit, wizardsWithSteps, isToolCallResolved, getCompletedToolResult, clearResolvedToolCalls, } = useSelectionWizard();
    const user = store.currentUser;
    const conversation = store.currentConversation;
    /** Latest conversation for tool callbacks (MobX may hold history while useChat is still empty after restore). */
    const conversationRef = useRef(conversation);
    conversationRef.current = conversation;
    const location = typeof window !== 'undefined' ? window.location?.href : '';
    // Get isDualSearchMode strictly when BOTH portal config enables dual
    // AND the widget layout requests dual
    const layoutMode = options?.layoutMode;
    const portalDualEnabled = store.clientAIConfig?.clientAIUseDualSearch || false;
    const isDualSearchMode = portalDualEnabled && layoutMode === 'dual';
    const disableConversationPersistence = Boolean(options?.disableConversationPersistence);
    const resetConversationOnNewInteraction = Boolean(options?.resetConversationOnNewInteraction);
    // Get suggestedPrompts from store
    const suggestedPrompts = store.suggestedPrompts || [];
    // Check if a new search query requires a conversation reset (only in Dual Search mode)
    // Simple logic: compare initialMessage with the first message in the conversation
    const shouldResetForNewSearch = useCallback((newQuery) => {
        if (!isDualSearchMode)
            return false;
        if (!newQuery)
            return false;
        // Get first message from existing conversation
        const firstMessage = conversation?.messages?.[0]?.content;
        if (!firstMessage)
            return false; // No conversation to reset
        // Compare normalized queries
        const normalizedNew = normalizeSearchQuery(newQuery);
        const normalizedFirst = normalizeSearchQuery(firstMessage);
        return normalizedNew !== normalizedFirst;
    }, [isDualSearchMode, conversation]);
    // Ref to track messages for use in callbacks
    const messagesRef = useRef([]);
    // Queue one user message to send only after a reset materializes a new conversation id.
    const pendingMessageAfterResetRef = useRef(null);
    /** `reset` is declared later; append/handleSubmit call this ref so TS order is valid and we always use latest reset. */
    const resetFnRef = useRef(() => { });
    // Ref to track the last synced message to avoid duplicates
    const lastSyncedMessageRef = useRef(null);
    // Throttle localStorage persistence during streaming. getSnapshot + JSON.stringify of
    // the conversation (including heavy itemReferences.productPayload) is expensive and
    // must not run on every token chunk — it freezes the main thread and blocks scrolling.
    const persistTimeoutRef = useRef(null);
    const persistPendingRef = useRef(false);
    // Ref to track pending exit tool call (reason) - will be sent after stream finishes
    const pendingExitToolRef = useRef(null);
    const exitPostedRef = useRef(false);
    const exitFallbackTimeoutRef = useRef(null);
    const clearExitFallback = useCallback(() => {
        if (exitFallbackTimeoutRef.current) {
            clearTimeout(exitFallbackTimeoutRef.current);
            exitFallbackTimeoutRef.current = null;
        }
    }, []);
    const persistConversation = useCallback(() => {
        if (disableConversationPersistence || typeof localStorage === 'undefined') {
            return;
        }
        const conv = conversationRef.current;
        if (!conv) {
            return;
        }
        try {
            localStorage.setItem(conversationKey(store), JSON.stringify(getSnapshot(conv)));
        }
        catch (err) {
            console.error('Error saving conversation to localStorage:', err);
        }
    }, [disableConversationPersistence, store]);
    const schedulePersist = useCallback((immediate) => {
        if (disableConversationPersistence || typeof localStorage === 'undefined') {
            return;
        }
        if (immediate) {
            if (persistTimeoutRef.current) {
                clearTimeout(persistTimeoutRef.current);
                persistTimeoutRef.current = null;
            }
            persistPendingRef.current = false;
            persistConversation();
            return;
        }
        persistPendingRef.current = true;
        if (persistTimeoutRef.current) {
            return;
        }
        persistTimeoutRef.current = setTimeout(() => {
            persistTimeoutRef.current = null;
            if (!persistPendingRef.current) {
                return;
            }
            persistPendingRef.current = false;
            persistConversation();
        }, 1500);
    }, [disableConversationPersistence, persistConversation]);
    useEffect(() => () => {
        if (persistTimeoutRef.current) {
            clearTimeout(persistTimeoutRef.current);
            persistTimeoutRef.current = null;
        }
    }, []);
    const postConversationExit = useCallback((reason, source) => {
        if (exitPostedRef.current) {
            return;
        }
        exitPostedRef.current = true;
        const exitTimestamp = Date.now();
        console.log('[ConversationExit] postMessage', {
            reason,
            exitTimestamp,
            exitTimestampISO: new Date(exitTimestamp).toISOString(),
            source,
        });
        if (typeof window !== 'undefined') {
            window.parent.postMessage({
                source: 'microcommerce-ai-embed',
                type: 'conversationExit',
                reason,
                timestamp: new Date(exitTimestamp).toISOString(),
            }, '*');
        }
        pendingExitToolRef.current = null;
        clearExitFallback();
    }, [clearExitFallback]);
    const { data, messages, input, handleInputChange, handleSubmit: baseHandleSubmit, append: baseAppend, isLoading: isPromptLoading, setMessages, setInput, addToolResult, } = useChat({
        id: conversation?.id,
        api: store.clientAIUrl,
        sendExtraMessageFields: true,
        maxSteps: 10,
        initialMessages: conversation ? conversation.messages : [],
        // Client-side tools: return value is used by the AI SDK as the tool result (see
        // @ai-sdk/ui-utils processStream onToolCallPart); the SDK then continues with that result.
        async onToolCall({ toolCall }) {
            // Ignore dynamic/unknown tools so we only handle our static client-side tools
            if (toolCall.dynamic) {
                return undefined;
            }
            // Client-side tool: checkOrderStatus
            if (toolCall.toolName === 'checkOrderStatus') {
                try {
                    const { status, activeOnly } = toolCall.args;
                    const response = await getOrderData({
                        status,
                        activeOnly,
                    });
                    if (Array.isArray(response.orders)) {
                        // console.log('[ClientSideTool] Orders received', { count: response.orders.length });
                        // Format orders for AI consumption
                        const formattedOrders = formatOrdersForAI(response.orders);
                        // console.log('[ClientSideTool] Orders formatted for AI', {
                        // count: formattedOrders.length
                        // });
                        return formattedOrders;
                    }
                    console.error('[ClientSideTool] Error fetching orders:', response?.error);
                    return undefined;
                }
                catch (fetchError) {
                    console.error('[ClientSideTool] Exception:', fetchError);
                    return undefined;
                }
            }
            // Client-side tool: exitConversation
            if (toolCall.toolName === 'exitConversation') {
                try {
                    const reason = toolCall.args?.reason;
                    // console.log('[ClientSideTool] Exit conversation', { reason });
                    // Mark exit tool as pending - will send postMessage after stream finishes
                    pendingExitToolRef.current = {
                        reason: reason || 'user_requested',
                    };
                    return {
                        conversationEnded: true,
                        reason: reason || 'user_requested',
                        message: 'Conversation ended',
                    };
                }
                catch (fetchError) {
                    console.error('[ClientSideTool] Exception:', fetchError);
                    return undefined;
                }
            }
            // Client-side tool: selectionWizard – wizard runs inline in the message area
            if (toolCall.toolName === 'selectionWizard') {
                try {
                    if (!requestInlineWizard) {
                        return { error: 'Selection wizard is not available.' };
                    }
                    const toolCallId = extractSelectionWizardToolCallId(toolCall);
                    // Cached suggested prompts reuse a static toolCallId; a duplicate onToolCall after the user
                    // finishes must not reopen the wizard — return the stored result so maxSteps can continue.
                    if (isToolCallResolved(toolCallId)) {
                        const cached = getCompletedToolResult(toolCallId);
                        if (cached != null)
                            return cached;
                    }
                    const wizardId = toolCall.args?.wizardId;
                    const result = await requestInlineWizard(toolCallId, wizardId);
                    if (!result) {
                        return { cancelled: true, message: 'User closed the wizard without completing.' };
                    }
                    return { summary: result.summary, answers: result.answers, wizardId: result.wizardId };
                }
                catch (fetchError) {
                    console.error('[ClientSideTool] selectionWizard exception:', fetchError);
                    return undefined;
                }
            }
            return undefined;
        },
        // override fetch to include the headers
        async fetch(inputText, init) {
            const headers = new Headers(init?.headers);
            // try {
            //   const currentSession = await Auth.currentSession();
            //   const accessToken = currentSession?.getAccessToken().toString();
            //   if (!accessToken) {
            //     console.log('No access token');
            //   } else {
            //     headers.set('X-Cognito-Token', accessToken);
            //   }
            // } catch (getTokenError) {
            //   console.error('Failed to get access token:', getTokenError);
            // }
            try {
                // Lambda does not support unsigned payloads
                // See https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/private-content-restricting-access-to-lambda.html#create-oac-overview-lambda
                if (conversation?.messages?.length === 0) {
                    conversation?.sendTrackingEvent('conversation_start');
                }
                conversation?.sendTrackingEvent('user_submit');
                const digestHex = await digestMessage(init?.body?.toString() || '');
                headers.append('X-Amz-Content-Sha256', digestHex);
                // console.log('fetching', inputText, { ...init, headers });
                const result = await fetch(inputText, { ...init, headers });
                if (result.status === 503) {
                    throw new Error('Service Temporarily Unavailable');
                }
                return result;
            }
            catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
                console.error('Fetch error:', errorMessage);
                conversation?.sendTrackingEvent('error');
                setError(errorMessage);
                throw new Error(errorMessage);
            }
        },
        onFinish: () => {
            conversation?.sendTrackingEvent('agent_response');
            // Only clear error if there's an assistant response
            const currentMessages = messagesRef.current;
            const lastMessage = currentMessages[currentMessages.length - 1];
            if (lastMessage?.role === 'assistant') {
                setError(null);
            }
            // Fallback: if render signal never arrives, post exit after a short delay
            if (pendingExitToolRef.current && !exitPostedRef.current) {
                clearExitFallback();
                const fallbackReason = pendingExitToolRef.current.reason || 'user_requested';
                exitFallbackTimeoutRef.current = setTimeout(() => {
                    if (!exitPostedRef.current) {
                        postConversationExit(fallbackReason, 'fallback_onFinish');
                    }
                }, 4000);
            }
        },
        onResponse: () => {
            conversation?.sendTrackingEvent('agent_response');
            // Only clear error if there's an assistant response
            const currentMessages = messagesRef.current;
            const lastMessage = currentMessages[currentMessages.length - 1];
            if (lastMessage?.role === 'assistant') {
                setError(null);
            }
        },
        experimental_prepareRequestBody: (requestOptions) => {
            const { messages: daMessages, 
            // requestData: daRequestData,
            // requestBody: daRequestBody,
            id: daId, } = requestOptions;
            // console.log('experimental_prepareRequestBody', {
            //   daMessages,
            //   daRequestData,
            //   daRequestBody,
            //   daId,
            // });
            return {
                arguments: buildClientAIRequestArguments({
                    conversationOptions: options || {
                        conversationType: 'shoppingAssistant',
                        layoutMode: 'ai',
                    },
                    organizationID: store.currentOrganizationID,
                    portalID: store.currentPortalID,
                    sessionID: conversation?.id,
                    userID: user?.id,
                    location,
                    customerContext: store.customerContext,
                    portalClientAIUseDualSearch: store.clientAIConfig?.clientAIUseDualSearch,
                    adminOptions: adminOptions,
                }),
                messages: daMessages,
                id: daId,
            };
        },
    });
    const isLoading = isPromptLoading;
    /**
     * Authoritative tool rows for wizard placement + main-column mode — derived from useChat, not MobX.
     * normalizeMessagesForAddToolResult merges `parts` / legacy rows so `toolInvocations` matches the SDK.
     */
    const wizardPlacementMessages = useMemo(() => {
        if (!messages?.length)
            return [];
        const normalized = normalizeMessagesForAddToolResult(messages);
        return normalized.map((m) => ({
            role: m.role,
            toolInvocations: m.toolInvocations,
        }));
    }, [messages]);
    /** Single source of truth for Conversation: wizard full-bleed vs message list (must align with useChat). */
    const selectionWizardInMainColumn = useMemo(() => shouldSelectionWizardOccupyMainColumn(wizardPlacementMessages, pendingInlineWizard, pendingComposerEdit, wizardsWithSteps.length, isToolCallResolved), [wizardPlacementMessages, pendingInlineWizard, pendingComposerEdit, wizardsWithSteps.length, isToolCallResolved]);
    // Keep messagesRef in sync with messages
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);
    /**
     * useChat only applies initialMessages on mount. After localStorage restore, MobX can
     * have the full thread while useChat messages are still [] — resumed wizard completion
     * would normalize an empty array and addToolResult would no-op. Hydrate from MobX.
     */
    useEffect(() => {
        if (!setMessages)
            return;
        if (messages.length > 0)
            return;
        const conv = conversationRef.current;
        if (!conv || conv.messages.length === 0)
            return;
        const snap = conv.messages.map((m) => getSnapshot(m));
        setMessages(normalizeMessagesForAddToolResult(snap));
    }, [conversation?.id, conversation?.messages.length, messages.length, setMessages]);
    useEffect(() => {
        // Reset exit state when conversation changes
        exitPostedRef.current = false;
        pendingExitToolRef.current = null;
        clearExitFallback();
    }, [conversation?.id, clearExitFallback]);
    useEffect(() => () => {
        clearExitFallback();
    }, [clearExitFallback]);
    useEffect(() => {
        conversation?.sendTrackingEvent('ready');
    }, [conversation]);
    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }
        const handleExitReady = (event) => {
            const customEvent = event;
            const detail = customEvent?.detail || {};
            // avoid firing if already posted
            if (exitPostedRef.current) {
                return;
            }
            const reason = pendingExitToolRef.current?.reason || detail.reason || 'user_requested';
            // allow messages to indicate lack of content; default is true
            if (detail.hasContent === false) {
                return;
            }
            postConversationExit(reason, 'render_ready');
        };
        window.addEventListener('microcommerce:conversationExitReady', handleExitReady);
        return () => {
            window.removeEventListener('microcommerce:conversationExitReady', handleExitReady);
        };
    }, [postConversationExit]);
    useEffect(() => {
        if (!store.currentConversation && typeof window !== 'undefined') {
            const storageKey = conversationKey(store);
            // Retrieve conversation and sessionID from localStorage
            // dont do this if code is running on the server
            const conversationSnapshot = disableConversationPersistence
                ? undefined
                : loadFromLocalStorage(storageKey);
            if (disableConversationPersistence && hasLocalStorage) {
                localStorage.removeItem(storageKey);
            }
            if (conversationSnapshot) {
                // console.log('conversationSnapshot', conversationSnapshot);
                // Check if sessionID exists and is valid
                if (!conversationSnapshot.sessionID && !conversationSnapshot.id) {
                    console.warn('conversationSnapshot is missing a valid sessionID. A new sessionID will be generated.');
                    conversationSnapshot.sessionID = v4();
                }
                // Align conversation.id with sessionID
                const newConversation = store.createConversation({
                    ...conversationSnapshot,
                    id: conversationSnapshot.sessionID || conversationSnapshot.id,
                });
                // console.log('Resuming conversation...', newConversation);
                store.setCurrentConversation(newConversation);
            }
            else {
                const newSessionID = v4();
                const newConversation = store.createConversation({
                    ...initialMessages,
                    id: newSessionID,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    messages: initialMessages?.messages || [],
                    agentName: options.agentName,
                });
                store.setCurrentConversation(newConversation);
            }
        }
        else {
            // console.log('conversation already exists', conversation);
        }
    }, [
        initialMessages,
        options?.agentName,
        store,
        store?.currentConversation,
        conversation,
        location,
        disableConversationPersistence,
    ]);
    // Wrap append to remove failed user message before adding new one
    const append = useCallback((message) => {
        if (message?.role === 'user') {
            clearResolvedToolCalls();
        }
        const hasExistingMessages = (conversationRef.current?.messages?.length || 0) > 0;
        const shouldResetBeforeAppend = Boolean(resetConversationOnNewInteraction
            && message?.role === 'user'
            && hasExistingMessages);
        if (shouldResetBeforeAppend) {
            const normalizedContent = typeof message?.content === 'string' ? message.content.trim() : '';
            if (!normalizedContent) {
                return;
            }
            pendingMessageAfterResetRef.current = {
                content: normalizedContent,
                previousConversationId: conversationRef.current?.id || null,
            };
            resetFnRef.current();
            return;
        }
        // Check if there's a failed user message
        // (last message is user, no assistant after, error exists)
        if (error && messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            if (lastMessage.role === 'user') {
                const userMessageIndex = messages.length - 1;
                const hasAssistantAfter = messages.slice(userMessageIndex + 1).some((m) => m.role === 'assistant');
                if (!hasAssistantAfter) {
                    // Remove the failed user message before appending the new one
                    if (setMessages) {
                        const newMessages = messages.slice(0, -1);
                        setMessages(newMessages);
                        // Clear error since user is retrying
                        setError(null);
                    }
                }
            }
        }
        // Now append the new message
        baseAppend(message);
    }, [error, messages, setMessages, baseAppend, resetConversationOnNewInteraction, clearResolvedToolCalls]);
    const handleSubmit = useCallback((e) => {
        const normalizedInput = typeof input === 'string' ? input.trim() : '';
        if (normalizedInput) {
            clearResolvedToolCalls();
        }
        const hasExistingMessages = (conversationRef.current?.messages?.length || 0) > 0;
        if (resetConversationOnNewInteraction && normalizedInput && hasExistingMessages) {
            e?.preventDefault?.();
            pendingMessageAfterResetRef.current = {
                content: normalizedInput,
                previousConversationId: conversationRef.current?.id || null,
            };
            resetFnRef.current();
            if (setInput) {
                setInput('');
            }
            return;
        }
        // Check if there's a failed user message
        // (last message is user, no assistant after, error exists)
        if (error && messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            if (lastMessage.role === 'user') {
                const userMessageIndex = messages.length - 1;
                const hasAssistantAfter = messages.slice(userMessageIndex + 1).some((m) => m.role === 'assistant');
                if (!hasAssistantAfter) {
                    // Remove the failed user message before submitting
                    if (setMessages) {
                        const newMessages = messages.slice(0, -1);
                        setMessages(newMessages);
                        // Clear error since user is retrying
                        setError(null);
                    }
                }
            }
        }
        // Now call the base handleSubmit
        baseHandleSubmit(e);
    }, [error, messages, setMessages, baseHandleSubmit, input, resetConversationOnNewInteraction, setInput, baseAppend, clearResolvedToolCalls]);
    useEffect(() => {
        const pending = pendingMessageAfterResetRef.current;
        if (!pending) {
            return;
        }
        const nextConversationId = conversation?.id || null;
        if (!nextConversationId) {
            return;
        }
        if (pending.previousConversationId && pending.previousConversationId === nextConversationId) {
            return;
        }
        pendingMessageAfterResetRef.current = null;
        baseAppend({
            role: 'user',
            content: pending.content,
        });
    }, [conversation?.id, baseAppend]);
    useEffect(() => {
        if (conversation) {
            // console.log('Connecting conversation...', { conversation });
            conversation.connect(append);
        }
    }, [append, conversation]);
    // Repopulate input with failed message content when error occurs
    useEffect(() => {
        if (!error || !messages?.length || !setInput) {
            return;
        }
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.role === 'user'
            && !isPromptLoading
            && input === '') {
            const userMessageIndex = messages.length - 1;
            const hasAssistantAfter = messages.slice(userMessageIndex + 1).some((m) => m.role === 'assistant');
            if (!hasAssistantAfter) {
                // Repopulate input with the failed message content
                setInput(lastMessage.content || '');
            }
        }
    }, [error, messages, isPromptLoading, setInput, input]);
    // useLayoutEffect so MobX matches useChat before paint — avoids flashing the welcome/empty
    // state after the selection wizard completes (useChat already has the thread; store was one frame behind).
    useLayoutEffect(() => {
        if (!messages?.length || messages.length === 0 || !conversation) {
            return;
        }
        const normalizedForStore = normalizeMessagesForAddToolResult(messages);
        const lastMessage = normalizedForStore[normalizedForStore.length - 1];
        // Full-array signature: earlier assistant turns (multi-step tools) must invalidate the skip.
        const messageSignature = normalizedForStore
            .map((m) => {
            const ti = m.toolInvocations;
            const td = ti?.length ? JSON.stringify(ti) : '';
            const textForSync = getChatMessageTextForSync(m);
            return `${m.role}:${String(m.id ?? '')}:${textForSync}:${td}`;
        })
            .join('\x1e');
        // Skip syncing if there's an error and last message is from user without assistant response
        if (error && lastMessage.role === 'user') {
            const userMessageIndex = normalizedForStore.length - 1;
            const hasAssistantAfter = normalizedForStore.slice(userMessageIndex + 1).some((m) => m.role === 'assistant');
            if (!hasAssistantAfter && !isPromptLoading) {
                return;
            }
        }
        if (lastSyncedMessageRef.current === messageSignature) {
            return;
        }
        conversation.syncMessagesFromChat(normalizedForStore);
        for (let i = 0; i < normalizedForStore.length; i += 1) {
            const m = normalizedForStore[i];
            if (m.role === 'function') {
                conversation.appendMessage(m);
            }
        }
        lastSyncedMessageRef.current = messageSignature;
        schedulePersist(!isPromptLoading);
    }, [
        conversation,
        messages,
        store,
        error,
        isPromptLoading,
        disableConversationPersistence,
        schedulePersist,
    ]);
    // Guarantee a final flush when streaming ends, even if no further message
    // signature change triggers the sync effect above.
    useEffect(() => {
        if (!isPromptLoading && persistPendingRef.current) {
            schedulePersist(true);
        }
    }, [isPromptLoading, schedulePersist]);
    const dataLength = data?.length;
    const conversationSystemPrompt = conversation?.systemPrompt;
    useEffect(() => {
        if (!dataLength || !conversation) {
            return;
        }
        const references = data[(dataLength || 1) - 1]?.itemReferences || {};
        // console.log('ConversationWidget.references', references);
        conversation.appendReferences(references);
        const newSystemPrompt = data[0]?.systemPrompt;
        const newModelID = data[0]?.modelID;
        // console.log('newSystemPrompt', newSystemPrompt);
        if (newSystemPrompt !== conversationSystemPrompt) {
            conversation.setSystemPrompt(newSystemPrompt);
        }
        if (newModelID && newModelID !== conversation.modelID) {
            conversation.setModelID(newModelID);
        }
    }, [conversation, conversationSystemPrompt, data, dataLength]);
    const reset = useCallback(() => {
        clearResolvedToolCalls();
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(conversationKey(store));
        }
        conversation?.sendTrackingEvent('reset');
        // Clear chat state before swapping conversations to avoid MST node reuse
        lastSyncedMessageRef.current = null;
        pendingExitToolRef.current = null;
        exitPostedRef.current = false;
        if (setMessages) {
            setMessages([]);
        }
        if (setInput) {
            setInput('');
        }
        store.setCurrentConversation(undefined);
        setError(null);
    }, [conversation, setMessages, setInput, store, clearResolvedToolCalls]);
    resetFnRef.current = reset;
    const saveFeedback = async (feedbackType, messageId, conversationId) => {
        try {
            // console.log(`Sending feedback: ${feedbackType} for message ${messageId}`);
            const feedbackPayload = {
                feedbackType,
                messageId,
                conversationId,
                user,
                location,
            };
            baseAppend({
                // @ts-expect-error TODO - change this to be a valid role
                role: 'function',
                content: JSON.stringify({ feedback: feedbackPayload, feedbackMessage: true }),
            });
        }
        catch (err) {
            console.error('Error saving feedback:', err);
        }
    };
    // Helper function to format SuggestedPrompt markup
    const formatSuggestedPrompt = (type, source, key, name, prompt) => {
        // For suggested and persistent types, no key/name needed
        if (type === 'suggested' || type === 'persistent') {
            return `<SuggestedPrompt type="${type}" source="${source}">
${prompt}
</SuggestedPrompt>`;
        }
        // For product and category types, key and name are required
        if (name && key && prompt) {
            return `<SuggestedPrompt type="${type}" source="${source}" key="${key}" name="${name}">
${prompt}
</SuggestedPrompt>`;
        }
        if (name && prompt) {
            return `For ${name}: ${prompt}`;
        }
        return prompt || '';
    };
    const handleSuggestedPromptClick = (prompt, type = 'product', source = 'host_page') => {
        let name;
        let key;
        if (type === 'product') {
            name = store.customerContext?.currentPage?.product?.name
                || store.customerContext?.currentPage?.title;
            const productHandle = store.customerContext?.currentPage?.product?.handle;
            key = productHandle || '';
        }
        else {
            name = store.customerContext?.currentPage?.collection?.name
                || store.customerContext?.currentPage?.title;
            const collectionHandle = store.customerContext?.currentPage?.collection?.handle;
            const collectionID = store.customerContext?.currentPage?.collection?.id;
            key = collectionID || collectionHandle || '';
        }
        // Key normalization: id > handle > URL (consistent with backend)
        const contentForAI = formatSuggestedPrompt(type, source, key, name, prompt);
        append({
            role: 'user',
            content: contentForAI,
        });
    };
    const submitSelectionWizardToolResult = useCallback((toolCallId, result) => {
        if (typeof addToolResult !== 'function' || !setMessages)
            return;
        // Restored conversations lack `parts` tool-invocation rows; addToolResult would no-op.
        // If useChat never hydrated (e.g. conversation loaded after mount), prev can be [] while MobX has the thread.
        setMessages((prev) => {
            const conv = conversationRef.current;
            const fromStore = conv?.messages?.length
                ? conv.messages.map((m) => getSnapshot(m))
                : [];
            const base = fromStore.length > prev.length ? fromStore : prev;
            return normalizeMessagesForAddToolResult(base);
        });
        window.setTimeout(() => {
            addToolResult({ toolCallId, result });
        }, 0);
    }, [addToolResult, setMessages]);
    return {
        append,
        conversation,
        handleInputChange,
        handleSubmit,
        input,
        isLoading,
        error,
        reset,
        saveFeedback,
        suggestedPrompts,
        handleSuggestedPromptClick,
        submitSelectionWizardToolResult,
        /** useChat thread length; can be ahead of MobX for one tick — use with sync layout effect */
        chatMessagesLength: messages.length,
        /** From useChat + wizard config — do not recompute from MobX in Conversation */
        selectionWizardInMainColumn,
        /** Pass to SelectionWizardComposerArea as `messages` (placement + edit link), always from useChat */
        wizardPlacementMessages,
        // Dual Search reset logic
        shouldResetForNewSearch,
        isDualSearchMode,
    };
};
export default useConversation;
//# sourceMappingURL=useConversation.js.map